import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { useGetDiscountPolicy } from 'src/features/discountPolicies/api/useGetDiscountPolicy';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import DeleteDiscountPolicyFormModal from 'src/features/discountPolicies/components/forms/DeleteDiscountPolicyFormModal';
import UpdateDiscountPolicyFormModal from 'src/features/discountPolicies/components/forms/UpdateDiscountPolicyFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const DiscountPolicyDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const { discountPolicyId = '' } = useParams();

    const permissionManageDiscounts = useHasPermissions([PERMISSION.MANAGE_DISCOUNTS]);
    const permissionAccessServicePlans = useHasPermissions([PERMISSION.ACCESS_SERVICE_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(discountPoliciesMessages.discountPolicyDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: discountPolicy } = useGetDiscountPolicy({
        policyId: discountPolicyId,
    });

    const isManualValidDurationAsString = discountPolicy?.manual_valid_duration ? 'true' : 'false';

    const formatValidDuration = (value: string) => {
        const [days] = value.split(' ');
        return `P${Number(days)}D`;
    };

    const dashboardBlockData = {
        section1: [
            {
                title: commonMessages.name,
                value: discountPolicy?.name ?? '-',
            },
            {
                title: generalMessages.validFrom,
                value: getFormattedDateTimeForDetailPanel(discountPolicy?.valid_from) ?? '-',
            },
            {
                title: generalMessages.validTo,
                value: discountPolicy?.valid_to ? getFormattedDateTimeForDetailPanel(discountPolicy?.valid_to) : '-',
            },
            {
                title: commonMessages.currency,
                value: discountPolicy?.currency ? <CellCurrencyBadge currency={discountPolicy?.currency} /> : '-',
            },

            {
                title: pricingPoliciesMessages.amount,
                value:
                    discountPolicy?.amount && discountPolicy.currency
                        ? formatNumber(discountPolicy?.amount / 100, {
                              style: 'currency',
                              currency: discountPolicy.currency,
                          })
                        : '-',
            },
            {
                title: discountPoliciesMessages.percent,
                value: discountPolicy?.percent ? `${(Number(discountPolicy.percent) * 100).toFixed(2)}%` : '-',
            },
        ],
        section2: [
            {
                title: discountPoliciesMessages.manualValidDuration,
                value: discountPolicy ? (
                    <Badge theme={booleanBadgeOptions[isManualValidDurationAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isManualValidDurationAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isManualValidDurationAsString].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: discountPoliciesMessages.validDuration,
                value: discountPolicy?.valid_duration ? formatValidDuration(discountPolicy?.valid_duration) : '-',
            },
            {
                title: discountPoliciesMessages.maxApplications,
                value: discountPolicy?.max_applications ?? '-',
            },
            {
                title: discountPoliciesMessages.maxApplicationsPerCustomer,
                value: discountPolicy?.max_applications_per_customer ?? '-',
            },
        ],
        section3: [
            {
                title: servicePlansMessages.servicePlans,
                value: discountPolicy?.service_plans?.length
                    ? discountPolicy?.service_plans?.map((plan) => {
                          return (
                              <LinkWithIcon
                                  to={`/service-plans/${plan.id}`}
                                  linkTitle={plan.name}
                                  hasPermissions={permissionAccessServicePlans}
                              />
                          );
                      })
                    : '-',
            },
            {
                title: servicePlanItemsMessages.servicePlanItems,
                value: discountPolicy?.service_plan_items?.length
                    ? discountPolicy?.service_plan_items?.map((item) => {
                          return (
                              <LinkWithIcon
                                  to={`/service-plan-item/${item.id}`}
                                  linkTitle={item.name}
                                  hasPermissions={permissionAccessServicePlans}
                              />
                          );
                      })
                    : '-',
            },
        ],
        section4: [
            {
                title: generalMessages.description,
                value: discountPolicy?.description ?? '-',
            },
        ],
    };

    return (
        <>
            <Button
                asChild
                className="place-self-start p-0"
                variant="link"
                onClick={() => navigate('/discount-policies')}
            >
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(discountPoliciesMessages.backToAllDiscountPolicies)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {discountPolicy?.name}
                        </SectionBlockTitle>
                        {permissionManageDiscounts && discountPolicy && (
                            <div className="flex flex-row gap-2">
                                <UpdateDiscountPolicyFormModal
                                    semantics={{
                                        entryName: formatMessage(discountPoliciesMessages.policy).toLowerCase(),
                                    }}
                                    selectedDiscountPolicy={discountPolicy}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.edit)}</span>
                                    </Button>
                                </UpdateDiscountPolicyFormModal>
                                <DeleteDiscountPolicyFormModal
                                    selectedPolicy={discountPolicy}
                                    semantics={{
                                        entryName: formatMessage(discountPoliciesMessages.policy).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteDiscountPolicyFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={3} />
                </SectionBlock>
            </div>
        </>
    );
};

export default DiscountPolicyDashboard;
